import { useEffect, useMemo, useCallback, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import Head from 'next/head';
import Script from 'next/script'
import { useSettings, useProductInventoryByHandle } from '@backpackjs/storefront';
import { useMatchMedia } from '../../hooks';

import { ProductDetails } from './ProductDetails';
import { ProductHeader } from './ProductHeader';
import { ProductMedia } from './ProductMedia';
import { useSelectedVariant } from './useSelectedVariant';
import { useDataLayerActions } from '../../hooks';

import { ProductReviews } from '../../snippets/ProductReviews';
import { ProductUpsellRecommendations } from '../../snippets/ProductUpsellRecommendations';
import { ProductSimilarRecommendations } from '../../snippets/ProductSimilarRecommendations';
import { ProductAddToCartBar } from './ProductAddToCartBar';
import { ProductBreadcrumbs } from './ProductBreadcrumbs';

export function ProductTemplate({ renderSections, product, footerInView }) {
  const {
    selectedVariant,
    setSelectedOptions,
    selectedOptions,
    selectedMedia,
    needsSelection,
    optionChanges,
    setOptionChanges,
  } = useSelectedVariant(product);
  const settings = useSettings();
  const sections = renderSections();
  const { sendViewProductEvent } = useDataLayerActions();
  const { inventory, success: inventoryFetched } = useProductInventoryByHandle({
    handle: selectedVariant?.product?.handle,
    withInventory: false,
  });

  const [groupedBreadcrumbs, setGroupedBreadcrumbs] = useState();

  const isMobileViewport = useMatchMedia('(max-width: 767px)');
  const [quantity, setQuantity] = useState(1);
  const [element1InView, setElement1InView] = useState(false);
  const [element2InView, setElement2InView] = useState(false);

  const { enabledLowInventory, lowInventoryQty, lowInventoryMessage, enabledCustomSoldOutQty } = settings?.product?.options || {};
  const lowInventoryNotification = { enabled: enabledLowInventory, qty: lowInventoryQty, message: lowInventoryMessage };
  const soldOutQty = enabledCustomSoldOutQty ? settings?.product?.options?.soldOutQty : 0;
  const isGiftCard = product?.isGiftCard;
  const isSoldOut = isGiftCard ? false : inventory?.totalInventory <= soldOutQty;

  const swatches = settings?.product?.colors?.swatches;
  const { disableGroupHistory, enabledSoldOutColorSwatch } = settings?.product?.options || {};

  const handleDecrement = useCallback(() => {
    if (quantity === 1) return;
    setQuantity(quantity - 1);
  }, [quantity]);

  const handleIncrement = useCallback(() => {
    setQuantity(quantity + 1);
  }, [quantity]);

  const smoothScrollTo = (targetId) => {
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      const offsetTop = targetElement.offsetTop;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
      targetElement.setAttribute('tabindex', '-1'); // Make it focusable
      targetElement.focus();
    }
  };

  const { productDetailAccordionsSection, belowTheFoldSections } =
    useMemo(() => {
      if (!sections) return {};
      return {
        productDetailAccordionsSection: sections.find(({ props }) => {
          return (
            props?.['data-comp'] === 'product-detail-accordions' &&
            !props?.hidden
          );
        }),
        belowTheFoldSections: sections.filter(({ props }) => {
          return props?.['data-comp'] !== 'product-detail-accordions';
        }),
      };
    }, [sections]);

  const { ref: refElement1 } = useInView({
    threshold: 0,
    triggerOnce: false,
    onChange: (inView) => {
      setElement1InView(inView);
    },
  });

  const { ref: refElement2 } = useInView({
    threshold: 0,
    triggerOnce: false,
    onChange: (inView) => {
      setElement2InView(inView);
    },
  });

  useEffect(() => {
    if (!selectedVariant) return;
    sendViewProductEvent({ product, selectedVariant, footerInView });
  }, [selectedVariant?.product?.handle, sendViewProductEvent, footerInView]);

  const stickyPromobar =
    settings?.header?.promobar?.enabled &&
    !settings?.header?.promobar?.autohide;
  const stickyTopClass = stickyPromobar
    ? 'md:top-[calc(var(--header-height)+var(--promobar-height)+2.5rem)] lg:top-[calc(var(--header-height)+var(--promobar-height)+2.5rem)] xl:top-[calc(var(--header-height)+var(--promobar-height)+3rem)]'
    : 'md:top-[calc(var(--header-height))] lg:top-[calc(var(--header-height)+2.5rem)] xl:top-[calc(var(--header-height)+2.5rem)]';

  const enabledProductCarousel = settings?.product?.productCarousel?.enabled;
  const enabledZoom = settings?.product?.productCarousel?.enableZoom;
  const enabledQuantitySelector = settings?.product?.quantitySelector?.enabled;
  const { productTitleMobilePlacement } = { ...settings?.product?.details };

  const { breadcrumbs, upsellProducts, similarProducts } = { ...settings?.product };
  const { enabledReviewWidget } = { ...settings?.product?.reviews };

  const heading = product?.title;
  
  const enabledsizeGuide =
    product.metafields &&
    product.metafields.find(
      (metafield) =>
        metafield.key === 'size_guide' && metafield.value === 'true'
    );
  const note = product.metafields?.find(
    (metafield) => metafield.key === 'note' && metafield.value.trim() !== ''
  );

  return (
    <>
      {heading && (
        <Head>
          <title>
            {heading && process.env.NEXT_PUBLIC_SITE_TITLE
              ? `${heading} | ${process.env.NEXT_PUBLIC_SITE_TITLE}`
              : (heading || process.env.NEXT_PUBLIC_SITE_TITLE || 'Rhoback')}
          </title>
          <meta name="oke:subscriber_id" content={process.env.NEXT_PUBLIC_OKENDO_USER_ID} />
        </Head>
      )}

      <Script
        src="https://cdn-static.okendo.io/reviews-widget-plus/js/okendo-reviews.js"
        strategy="afterInteractive"
        onLoad={() => console.log('Okendo script loaded successfully')}
        onError={(e) => console.error('Error loading Okendo script:', e)}
      />

      <>
        {breadcrumbs?.enabled && !isGiftCard && (
          <ProductBreadcrumbs
            breadcrumbs={breadcrumbs}
            product={product}
            setGroupedBreadcrumbs={setGroupedBreadcrumbs}
            enabledProductCarousel={enabledProductCarousel}
          />
        )}

        <div className={`px-contained py-contained ${productTitleMobilePlacement === 'underImage' ? 'max-md:!pt-0' : ''}`}>
          <div className={`mx-auto max-md:flex max-md:flex-col md:grid md:grid-cols-12 md:grid-rows-1 md:gap-5 2xl:gap-10 ${enabledProductCarousel ? 'max-w-[var(--content-max-lg-width)]' : 'max-w-[var(--content-max-xl-width)]'}`}>
            {/* mobile header placement */}
            {/* note: remove this component if mobile header shares same placement as desktop */}
            {isMobileViewport && productTitleMobilePlacement === 'aboveImage' ? (
              <ProductHeader
                isMobile
                isMobileViewport={isMobileViewport}
                product={product}
                isGiftCard={isGiftCard}
                selectedVariant={selectedVariant}
                note={note}
                headerExtraClasses="pb-5"
              />
            ) : null}

            <div className={`col-span-12 max-md:-mx-5 ${enabledProductCarousel ? 'md:col-span-6 2xl:col-span-7' : 'md:col-span-7'}`}>
              <div className={`md:sticky ${stickyTopClass} overflow-hidden`}>
                <ProductMedia
                  enabledProductCarousel={enabledProductCarousel}
                  enabledZoom={enabledZoom}
                  product={product}
                  selectedMedia={selectedMedia}
                  selectedVariant={selectedVariant}
                  soldOutQty={soldOutQty}
                />
                {upsellProducts?.upsellVisibility === 'underProductGallery' && !isMobileViewport && (
                  <div className="w-full mt-10">
                    <ProductUpsellRecommendations
                      legacyResourceId={product?.legacyResourceId}
                      upsellProducts={upsellProducts}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className={`col-span-12 ${enabledProductCarousel ? 'md:col-span-6 md:col-start-7 2xl:col-span-5 2xl:col-start-8' : 'md:col-span-5 md:col-start-8'}`}>
              <div
                className={`md:sticky ${stickyTopClass}`}
              >
                <div className="flex flex-col bg-white gap-y-2.5 md:gap-y-5">
                  {/* desktop header placement */}
                  <ProductHeader
                    isMobileViewport={isMobileViewport}
                    product={product}
                    isGiftCard={isGiftCard}
                    selectedVariant={selectedVariant}
                    productTitleMobilePlacement={ productTitleMobilePlacement}
                    smoothScrollTo={smoothScrollTo}
                    note={note}
                    headerExtraClasses="max-md:pt-5"
                  />

                  <ProductDetails
                    isMobileViewport={isMobileViewport}
                    enabledQuantitySelector={enabledQuantitySelector}
                    enabledTrustBuilders={
                      settings?.product?.trustBuilders?.enabled
                    }
                    product={product}
                    productDetailAccordionsSection={productDetailAccordionsSection}
                    selectedOptions={selectedOptions}
                    selectedVariant={selectedVariant}
                    setSelectedOptions={setSelectedOptions}
                    isGiftCard={isGiftCard}
                    inventory={inventory}
                    inventoryFetched={inventoryFetched}
                    quantity={quantity}
                    handleDecrement={handleDecrement}
                    handleIncrement={handleIncrement}
                    refElement={refElement1}
                    lowInventoryNotification={lowInventoryNotification}
                    enabledsizeGuide={enabledsizeGuide}
                    sizeGuides={
                      settings?.product?.sizeGuides
                    }
                    swatches={swatches}
                    disableGroupHistory={disableGroupHistory}
                    enabledSoldOutColorSwatch={enabledSoldOutColorSwatch}
                    isSoldOut={isSoldOut}
                    soldOutQty={soldOutQty}
                    enabledGroupTabs={settings?.product?.options?.groupTabs?.enabledGroupTabs}
                    enabledGroupTabsLink={settings?.product?.options?.groupTabs?.enabledGroupTabsLink}
                    productTypeForGroupTabs={settings?.product?.options?.groupTabs?.productTypeForGroupTabs}
                    enabledGroupOptions={settings?.product?.options?.groupOptions?.enabledGroupOptions}
                    enabledGroupOptionsLink={settings?.product?.options?.groupOptions?.enabledGroupOptionsLink}
                    productTypeForGroupOptions={settings?.product?.options?.groupOptions?.productTypeForGroupOptions}
                    needsSelection={needsSelection}
                    optionChanges={optionChanges}
                    setOptionChanges={setOptionChanges}
                    groupedBreadcrumbs={groupedBreadcrumbs}
                  />
                </div>
                {
                  (upsellProducts?.upsellVisibility === 'underProductDetails' ||
                  (upsellProducts?.upsellVisibility === 'underProductGallery' && isMobileViewport)) && (
                    <ProductUpsellRecommendations
                      legacyResourceId={product?.legacyResourceId}
                      upsellProducts={upsellProducts}
                    />
                  )
                }
              </div>
            </div>

            {enabledReviewWidget ? (
              <div className="col-span-12">
                <ProductReviews 
                  productId={product?.legacyResourceId}
                  stickyTopClass={stickyTopClass}
                />
              </div>
            ) : null}

          </div>
        </div>

        {belowTheFoldSections || null}

        {isMobileViewport && (
          <ProductAddToCartBar
            selectedVariant={selectedVariant}
            quantity={quantity}
            isMobileViewport={isMobileViewport}
            elementInView={element1InView || element2InView || footerInView}
            optionChanges={optionChanges}
            needsSelection={needsSelection}
            smoothScrollTo={smoothScrollTo}
            forceSoldOut={isSoldOut}
          />
        )}

        {similarProducts?.enabled && (
          <div ref={refElement2} className="col-span-12">
            <div>
              <ProductSimilarRecommendations
                legacyResourceId={product?.legacyResourceId}
                similarProducts={similarProducts}
              />
            </div>
          </div>
        )}
      </>
    </>

  );
}

ProductTemplate.displayName = 'ProductTemplate';
